/* eslint-disable i18next/no-literal-string */
// src/components/errors/ErrorAnalyticsCharts.jsx
import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Box,
  Skeleton,
  useTheme,
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';
import { tokens } from '../../../theme';

export default function ErrorAnalyticsCharts({
  summary,
  loading = false,
}) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  if (loading || !summary) {
    return (
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Skeleton variant="text" width="60%" height={32} sx={{ mb: 2 }} />
              <Skeleton variant="rectangular" height={300} />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Skeleton variant="text" width="60%" height={32} sx={{ mb: 2 }} />
              <Skeleton variant="rectangular" height={300} />
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  }

  // Prepare data for error codes chart
  const errorCodesData = Object.entries(summary.errors_by_code || {})
    .map(([code, count]) => ({
      code,
      count,
      percentage: ((count / summary.total_errors) * 100).toFixed(1),
    }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 10); // Top 10 error codes

  // Prepare data for endpoints chart
  const endpointsData = Object.entries(summary.errors_by_endpoint || {})
    .map(([endpoint, count]) => {
      // Extract just the path from "METHOD /path"
      const parts = endpoint.split(' ');
      const path = parts.length > 1 ? parts[1] : endpoint;
      const shortPath = path.length > 30 ? `...${path.slice(-27)}` : path;
      
      return {
        endpoint: shortPath,
        fullEndpoint: endpoint,
        count,
        percentage: ((count / summary.total_errors) * 100).toFixed(1),
      };
    })
    .sort((a, b) => b.count - a.count)
    .slice(0, 8); // Top 8 endpoints

  // Color palette for charts
  const chartColors = [
    colors.redAccent[500],
    colors.yellowAccent[500],
    colors.blueAccent[500],
    colors.greenAccent[500],
    colors.pinkAccent[500],
    colors.redAccent[300],
    colors.yellowAccent[300],
    colors.blueAccent[300],
    colors.greenAccent[300],
    colors.pinkAccent[300],
  ];

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <Box
          sx={{
            backgroundColor: theme.palette.background.paper,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 1,
            p: 2,
            boxShadow: theme.shadows[4],
          }}
        >
          <Typography variant="body2" fontWeight="medium">
            {label}
          </Typography>
          <Typography variant="body2" color={payload[0].color}>
            Count: {payload[0].value}
          </Typography>
          {payload[0].payload.percentage && (
            <Typography variant="caption" color="text.secondary">
              {payload[0].payload.percentage}% of total errors
            </Typography>
          )}
        </Box>
      );
    }
    return null;
  };

  const CustomPieTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <Box
          sx={{
            backgroundColor: theme.palette.background.paper,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 1,
            p: 2,
            boxShadow: theme.shadows[4],
          }}
        >
          <Typography variant="body2" fontWeight="medium">
            {data.code}
          </Typography>
          <Typography variant="body2" color={payload[0].color}>
            Count: {data.count}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {data.percentage}% of total errors
          </Typography>
        </Box>
      );
    }
    return null;
  };

  return (
    <Grid container spacing={3} sx={{ mb: 3 }}>
      {/* Error Codes Distribution (Pie Chart) */}
      <Grid item xs={12} md={6}>
        <Card sx={{ height: 400 }}>
          <CardContent sx={{ height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              Error Codes Distribution
            </Typography>
            {errorCodesData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={errorCodesData}
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    fill={colors.primary[500]}
                    dataKey="count"
                    label={({ code, percentage }) => `${code} (${percentage}%)`}
                    labelLine={false}
                  >
                    {errorCodesData.map((entry, index) => (
                      <Cell 
                        key={`cell-${index}`} 
                        fill={chartColors[index % chartColors.length]} 
                      />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomPieTooltip />} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                height={300}
              >
                <Typography variant="body2" color="text.secondary">
                  No error data available
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      </Grid>

      {/* Top Endpoints by Error Count (Bar Chart) */}
      <Grid item xs={12} md={6}>
        <Card sx={{ height: 400 }}>
          <CardContent sx={{ height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              Top Error-Prone Endpoints
            </Typography>
            {endpointsData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart
                  data={endpointsData}
                  margin={{
                    top: 5,
                    right: 30,
                    left: 20,
                    bottom: 60,
                  }}
                >
                  <CartesianGrid 
                    strokeDasharray="3 3" 
                    stroke={colors.grey[700]}
                  />
                  <XAxis
                    dataKey="endpoint"
                    angle={-45}
                    textAnchor="end"
                    height={60}
                    fontSize={12}
                    stroke={theme.palette.text.secondary}
                  />
                  <YAxis 
                    stroke={theme.palette.text.secondary}
                    fontSize={12}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar 
                    dataKey="count" 
                    fill={colors.redAccent[500]}
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                height={300}
              >
                <Typography variant="body2" color="text.secondary">
                  No endpoint data available
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      </Grid>

      {/* Error Trends Over Time (if we have hourly data) */}
      {summary.errors_by_hour && (
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Error Trends (Last 24 Hours)
              </Typography>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart
                  data={Object.entries(summary.errors_by_hour).map(([hour, count]) => ({
                    hour: `${hour}:00`,
                    count,
                  }))}
                  margin={{
                    top: 5,
                    right: 30,
                    left: 20,
                    bottom: 5,
                  }}
                >
                  <CartesianGrid 
                    strokeDasharray="3 3" 
                    stroke={colors.grey[700]}
                  />
                  <XAxis
                    dataKey="hour"
                    stroke={theme.palette.text.secondary}
                    fontSize={12}
                  />
                  <YAxis 
                    stroke={theme.palette.text.secondary}
                    fontSize={12}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar 
                    dataKey="count" 
                    fill={colors.blueAccent[500]}
                    radius={[2, 2, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
      )}

      {/* Summary Statistics */}
      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Quick Statistics
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={3}>
                <Box textAlign="center">
                  <Typography variant="h4" color="error.main" fontWeight="bold">
                    {summary.total_errors || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Errors
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Box textAlign="center">
                  <Typography variant="h4" color="warning.main" fontWeight="bold">
                    {Object.keys(summary.errors_by_code || {}).length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Unique Error Types
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Box textAlign="center">
                  <Typography variant="h4" color="info.main" fontWeight="bold">
                    {Object.keys(summary.errors_by_endpoint || {}).length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Affected Endpoints
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Box textAlign="center">
                  <Typography variant="h4" color="success.main" fontWeight="bold">
                    {summary.period_hours || 0}h
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Analysis Period
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
}